import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { getString } from 'app/shared/utils/string.util';
import { HDInsightLinkedService } from 'app/model/linkedservice/hdinsight.linkedservice';
import { AzureStorageLinkedService } from 'app/model/linkedservice/azure-storage.linkedservice';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';
import { GlobalVariables } from 'app/services/globals';
import { HDInsightClusterAuthTypes, Credential } from 'shell-models-lib';
import { IHdinsightClusterAuthObject } from 'app/extensibility/metadatas/control/hdinsight-account-select-metadata';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(HDInsightLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                clusterUri: '*',
                userName: '*',
                isEspEnabled: '*',
                fileSystem: '*',
                password: 'secret',
                linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
                hcatalogLinkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
                encryptedCredential: '*',
                clusterAuthType: GlobalVariables.enableHDIClusterMIAuth ? '*' : undefined,
                credential: GlobalVariables.enableHDIClusterMIAuth ? '*' : undefined
            }
        }
    }
})
export class HDInsightLinkedServiceAdapter extends LinkedServiceAdapter<HDInsightLinkedService> {
    protected modelConstructor(): new () => HDInsightLinkedService {
        return HDInsightLinkedService;
    }

    protected model(): IModel {
        return HDInsightLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(hdinsightLinkedService: HDInsightLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.HDInsightLinkedService;

        properties.type = Metamodel.modelFor(HDInsightLinkedService).name;
        if (GlobalVariables.enableHDIClusterMIAuth){
            if (hdinsightLinkedService.clusterAuthObject !== undefined) {
                // When customer uses Azure radio-button, clusterAuthObject will be defined.
                // When customer uses Manual radio-button, clusterAuthObject will be un-defined.

                const authObject: IHdinsightClusterAuthObject = hdinsightLinkedService.clusterAuthObject;
                hdinsightLinkedService.clusterUri = authObject.clusterUri;
                hdinsightLinkedService.clusterAuthType = authObject.clusterAuthType;
                hdinsightLinkedService.userName = (authObject.clusterAuthType === HDInsightClusterAuthTypes.BasicAuth) ? authObject.userName : undefined;
                hdinsightLinkedService.password = (authObject.clusterAuthType === HDInsightClusterAuthTypes.BasicAuth) ? authObject.password : undefined;
                hdinsightLinkedService.credential = (authObject.clusterAuthType === HDInsightClusterAuthTypes.UserAssignedManagedIdentity) ? authObject.credential : undefined;
                hdinsightLinkedService.clusterAuthObject = undefined; // Clear the clusterAuthObject after extracting values
            }

            properties.typeProperties = {
                clusterUri:
                    hdinsightLinkedService.clusterUri !== undefined && hdinsightLinkedService.clusterUri !== ''
                        ? getString(hdinsightLinkedService.clusterUri)
                        : undefined,
                clusterAuthType:
                    hdinsightLinkedService.clusterAuthType !== undefined &&
                    hdinsightLinkedService.clusterAuthType !== ''
                        ? getString(hdinsightLinkedService.clusterAuthType)
                        : undefined,
                userName:
                    hdinsightLinkedService.clusterAuthType === HDInsightClusterAuthTypes.BasicAuth &&
                    hdinsightLinkedService.userName !== undefined &&
                    hdinsightLinkedService.userName !== ''
                        ? getString(hdinsightLinkedService.userName)
                        : undefined,
                password:
                    hdinsightLinkedService.clusterAuthType === HDInsightClusterAuthTypes.BasicAuth
                        ? passwordOrAKVtoSecretBase(hdinsightLinkedService.password, this.context)
                        : undefined,
                credential:
                    hdinsightLinkedService.clusterAuthType === HDInsightClusterAuthTypes.UserAssignedManagedIdentity &&
                    hdinsightLinkedService.credential != null
                        ? (ReferenceAdapter.toResource(
                              hdinsightLinkedService.credential,
                              this.context
                          ) as Model.CredentialReference)
                        : undefined
            };
        }
        else {
            properties.typeProperties = {
                clusterUri:
                    hdinsightLinkedService.clusterUri !== undefined && hdinsightLinkedService.clusterUri !== ''
                        ? getString(hdinsightLinkedService.clusterUri)
                        : undefined,
                userName:
                    hdinsightLinkedService.userName !== undefined && hdinsightLinkedService.userName !== ''
                        ? getString(hdinsightLinkedService.userName)
                        : undefined,
                password: passwordOrAKVtoSecretBase(hdinsightLinkedService.password, this.context)
            };
        }
        
        properties.typeProperties.isEspEnabled = hdinsightLinkedService.isEspEnabled;
        if (hdinsightLinkedService.fileSystem) {
            properties.typeProperties.fileSystem = hdinsightLinkedService.fileSystem;
        }
        if (hdinsightLinkedService.azureStorageLinkedService != null) {
            properties.typeProperties.linkedServiceName = ReferenceAdapter.toResource(hdinsightLinkedService.azureStorageLinkedService, this.context) as Model.LinkedServiceReference;
        }
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, hdinsightlinkedService?: HDInsightLinkedService): Promise<HDInsightLinkedService> {
        const properties = linkedServiceResource.properties as Model.HDInsightLinkedService;
        
        hdinsightlinkedService.clusterUri = properties.typeProperties.clusterUri;
        if (GlobalVariables.enableHDIClusterMIAuth){
            if (properties.typeProperties.clusterAuthType !== undefined) {
                hdinsightlinkedService.clusterAuthType = properties.typeProperties.clusterAuthType;
                if (properties.typeProperties.credential) {
                    hdinsightlinkedService.credential = ReferenceAdapter.toProxyModel<Credential>(Metamodel.modelFor(Credential), properties.typeProperties.credential, this.context);
                }
            }
            else {
                properties.typeProperties.clusterAuthType = HDInsightClusterAuthTypes.BasicAuth;
                hdinsightlinkedService.clusterAuthType = properties.typeProperties.clusterAuthType;   
            }
        }
        
        hdinsightlinkedService.userName = properties.typeProperties.userName;
        hdinsightlinkedService.password = secretBaseToPasswordOrAKV(properties.typeProperties.password, true, this.context);
        
        hdinsightlinkedService.isEspEnabled = properties.typeProperties.isEspEnabled;
        if (properties.typeProperties.fileSystem) {
            hdinsightlinkedService.fileSystem = properties.typeProperties.fileSystem;
        }
        if (properties.typeProperties.linkedServiceName != null) {
            hdinsightlinkedService.azureStorageLinkedService = ReferenceAdapter.toProxyModel<AzureStorageLinkedService>(Metamodel.modelFor(AzureStorageLinkedService),
                properties.typeProperties.linkedServiceName, this.context);
        }
        return hdinsightlinkedService;
    }
}
